/**
 * Matchweek grouping for season picks (specs/competitions-hub-client AC-5.2).
 * Pure — operates on the minimal structural shape of API match rows.
 */

export interface MatchweekMatchLike {
  matchId: string;
  round: string | null;
  status: 'upcoming' | 'live' | 'finished';
  displayOrder: number;
}

export interface Matchweek<T extends MatchweekMatchLike = MatchweekMatchLike> {
  round: string;
  label: string;
  matches: T[];
}

/**
 * Sort rank for a match within its matchweek (AC-2.9): live first, then still-to-play,
 * then finished. A matchday spans three days, so ordering on `displayOrder` alone buries
 * tomorrow's editable card under yesterday's settled ones — the cards you can still act on
 * end up hardest to reach.
 */
function matchweekSortRank(status: MatchweekMatchLike['status']): number {
  if (status === 'live') return 0;
  if (status === 'finished') return 2;
  return 1;
}

/** Groups matches by round, numerically sorted; non-numeric rounds are dropped. */
export function buildMatchweeks<T extends MatchweekMatchLike>(matches: readonly T[]): Matchweek<T>[] {
  const byRound = new Map<number, T[]>();
  for (const match of matches) {
    const n = Number.parseInt(match.round ?? '', 10);
    if (!Number.isInteger(n) || n <= 0) continue;
    const bucket = byRound.get(n);
    if (bucket) bucket.push(match);
    else byRound.set(n, [match]);
  }
  return [...byRound.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([n, ms]) => ({
      round: String(n),
      label: `Matchweek ${n}`,
      // State before time, then `displayOrder` within each state (AC-2.9). `displayOrder`
      // keeps its meaning as the chronological key — `defaultMatchweekRound` and
      // `matchweekStatus` read its VALUE, not this array's order, so both are unaffected.
      matches: [...ms].sort(
        (a, b) => (matchweekSortRank(a.status) - matchweekSortRank(b.status))
          || (a.displayOrder - b.displayOrder),
      ),
    }));
}

/**
 * The matchweek holding the NEXT kickoff; when everything has finished, the most
 * recently played one (AC-13.1/13.2). Null when there are no matchweeks.
 *
 * Ordering is by `displayOrder`, which the season linker assigns as the index over
 * date-sorted fixtures — so it is a faithful chronological key, while `round` is not.
 * Real leagues reschedule: MLS carries a matchweek 3 fixture in August, sitting
 * between MW18 and MW19 in time. Scanning matchweeks in numeric order (the previous
 * behaviour) opened any mid-season pool on that straggler instead of the live week.
 */
export function defaultMatchweekRound(matchweeks: readonly Matchweek[]): string | null {
  if (matchweeks.length === 0) return null;

  let nextRound: string | null = null;
  let nextOrder = Number.POSITIVE_INFINITY;
  let lastRound: string | null = null;
  let lastOrder = Number.NEGATIVE_INFINITY;

  for (const mw of matchweeks) {
    for (const match of mw.matches) {
      if (match.status !== 'finished' && match.displayOrder < nextOrder) {
        nextOrder = match.displayOrder;
        nextRound = mw.round;
      }
      // `>=` so that when displayOrder ties (fixtures sharing a kickoff, or a
      // fixture list that has not been ordered yet) the LATER matchweek wins —
      // "most recently played" should not resolve to matchweek 1.
      if (match.displayOrder >= lastOrder) {
        lastOrder = match.displayOrder;
        lastRound = mw.round;
      }
    }
  }

  return nextRound ?? lastRound ?? matchweeks[matchweeks.length - 1].round;
}

export type MatchweekStatus = 'finished' | 'live' | 'upcoming';

/**
 * Rolled-up state for a matchweek chip (AC-13.5): live if any match is in play,
 * finished only when every match has ended, else upcoming.
 */
export function matchweekStatus(matchweek: Matchweek): MatchweekStatus {
  if (matchweek.matches.length === 0) return 'upcoming';
  if (matchweek.matches.some((m) => m.status === 'live')) return 'live';
  if (matchweek.matches.every((m) => m.status === 'finished')) return 'finished';
  return 'upcoming';
}
